import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _uniqueId from "lodash/uniqueId";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useState } from 'react';
import { View, Text, Page, Image } from '@react-pdf/renderer';
import { Both, Graphs, Listings, scores } from '@mummssoftware/utils/constants';
import { PrintFooterPDFComponent } from '../Footer';
import { PrintHeaderPDFComponent } from '../Header';
import { fromDateToDate, styles } from '../../utils';
import { MeasuresDatePDF } from './components/MeasuresDatePDF';
import { MeasuresRecordedByPDF } from './components/MeasuresRecordedByPDF';
import { MeasuresUnitsPDF } from './components/MeasuresUnitsPDF';
import { MeasuresTypePDF } from './components/MeasuresTypePDF';
import { MeasuresCommentsPDF } from './components/MeasuresCommentsPDF';
import defaults from '../../translations/en.json';

const NoMeasuresDataPDF = ({
  dashboardPrintConfig
}) => /*#__PURE__*/React.createElement(View, {
  style: [styles.width100, styles.textAlignCenter, styles.marginYPointTwoCm]
}, /*#__PURE__*/React.createElement(Text, {
  style: styles.CPFTableDetailsText
}, dashboardPrintConfig && dashboardPrintConfig.fromDate && dashboardPrintConfig.toDate && defaults.noMeasuresDateRange || defaults.noMeasuresFound));

const MeasuresListingTable = ({
  graphImage,
  graphTableData,
  listingsOnly,
  displaySectionTitle
}) => {
  const row = (measure, sectionTitle) => /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.borderBottomBlack, styles.pYPointOneCm, styles.alignItemsCenter]
  }, /*#__PURE__*/React.createElement(MeasuresDatePDF, {
    measure: measure
  }), /*#__PURE__*/React.createElement(MeasuresRecordedByPDF, {
    measure: measure
  }), sectionTitle !== scores && /*#__PURE__*/React.createElement(MeasuresUnitsPDF, {
    measure: measure
  }), /*#__PURE__*/React.createElement(MeasuresTypePDF, {
    measure: measure
  }), /*#__PURE__*/React.createElement(MeasuresCommentsPDF, {
    measure: measure
  }));

  const graphMeasureType = graphImage.singleGraphTitles[0];
  const graphMeasureTypeCapitalized = graphMeasureType.charAt(0).toUpperCase() + graphMeasureType.slice(1);
  const sectionTitle = graphImage.singleGraphTitles[1];
  const sectionTitleAllCaps = sectionTitle.toUpperCase();
  return /*#__PURE__*/React.createElement(View, {
    style: listingsOnly ? styles.marginTopPointFiveCm : styles.marginTopPointTwoCm
  }, displaySectionTitle && /*#__PURE__*/React.createElement(View, {
    style: [styles.textAlignCenter, styles.width100, styles.marginBottomPointFiveCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontWeightBold, styles.fontSizeTwelvePt]
  }, sectionTitleAllCaps)), /*#__PURE__*/React.createElement(View, {
    style: [styles.row, styles.width100, styles.borderBottomBlack]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width25]
  }, defaults.date), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width25]
  }, defaults.recordedBy), sectionTitle !== scores && /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width12point5]
  }, defaults.units), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width25]
  }, graphMeasureTypeCapitalized), /*#__PURE__*/React.createElement(Text, {
    style: [styles.CPFTableRowHeaderText, styles.width12point5]
  }, defaults.comments)), graphTableData.map(measure => {
    const uniqueRowId = _uniqueId('measures-row-');

    return /*#__PURE__*/React.createElement(View, {
      key: uniqueRowId
    }, row(measure, sectionTitle));
  }));
};
/*
jsonMeasures.measures is sorted by default into four categories
(limitations, misc, scores, and vitals).
We want to consolidate the measures in each of those categories into one top-level dto.
*/


const getFlattenedMeasures = measures => {
  const allMeasures = {};

  if (measures) {
    Object.keys(measures).forEach(categoryName => {
      const groupedMeasures = measures[categoryName];
      Object.keys(groupedMeasures).forEach(measureName => {
        allMeasures[measureName] = groupedMeasures[measureName];
      });
    });
  }

  return allMeasures;
};

var _ref = /*#__PURE__*/React.createElement(PrintFooterPDFComponent, null);

export const MeasuresPDF = ({
  dashboardPrintConfig,
  measuresGlobal,
  pdfMeasuresGraphImages,
  browser,
  patient,
  jsonMeasures
}) => {
  const [measuresState, setMeasuresState] = useState(jsonMeasures || {});
  const [flattenedMeasures, setFlattenedMeasures] = useState(getFlattenedMeasures(measuresState.measures));
  /* 
  MeasuresPrint depends on jsonMeasures being conditionally nullified for its logic.
  We don't care about that logic here, so we just want the non-null data it got from the
  API call in getAsyncPDFData. measuresState will hold that data, and will never be null.
   */

  useEffect(() => {
    if (jsonMeasures) setMeasuresState(jsonMeasures);
  }, [jsonMeasures]);
  useEffect(() => {
    const {
      measures
    } = measuresState;
    setFlattenedMeasures(getFlattenedMeasures(measures));
  }, [measuresState]);
  const MeasuresGraphsWithTables = measuresGlobal && (pdfMeasuresGraphImages === null || pdfMeasuresGraphImages === void 0 ? void 0 : pdfMeasuresGraphImages.map(graphImage => {
    const singularGraphTitle = graphImage.singleGraphTitles[0];
    return {
      graphImage,
      graphTableData: flattenedMeasures && flattenedMeasures[singularGraphTitle]
    };
  }));

  const shallowDashboardPrintConfigView = dashboardPrintConfig && _objectSpread({}, dashboardPrintConfig);

  const MeasuresPrintView = shallowDashboardPrintConfigView === null || shallowDashboardPrintConfigView === void 0 ? void 0 : shallowDashboardPrintConfigView.Measures;
  const MeasuresRadioView = MeasuresPrintView.view;
  const measuresPopulated = pdfMeasuresGraphImages && pdfMeasuresGraphImages.length > 0;
  return /*#__PURE__*/React.createElement(Page, {
    wrap: false,
    size: "A4",
    orientation: "portrait",
    style: browser && browser.name === 'safari' || browser && browser.name === 'ios' ? styles.pageSafari : styles.page
  }, /*#__PURE__*/React.createElement(PrintHeaderPDFComponent, {
    browser: browser,
    patient: patient
  }), /*#__PURE__*/React.createElement(View, {
    style: [styles.textAlignCenter, styles.width100, styles.marginBottomPointFiveCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: [styles.printerHeaderText]
  }, defaults.measures), dashboardPrintConfig && dashboardPrintConfig.fromDate && dashboardPrintConfig.toDate && /*#__PURE__*/React.createElement(Text, {
    style: [styles.fontFamilyItalic, styles.fontSizeTenPt, styles.marginYPointTwoCm]
  }, fromDateToDate(dashboardPrintConfig))), MeasuresRadioView === Graphs && pdfMeasuresGraphImages && pdfMeasuresGraphImages.length > 0 && pdfMeasuresGraphImages.map((b64PDF, i) => {
    const uniqueIdGraph = _uniqueId('measures-graph-');

    return /*#__PURE__*/React.createElement(View, {
      break: b64PDF.breakSection && i !== 0,
      key: uniqueIdGraph,
      style: styles.pYPointOneCm
    }, /*#__PURE__*/React.createElement(Image, {
      source: b64PDF.src
    }));
  }) || measuresPopulated && MeasuresRadioView === Listings && MeasuresGraphsWithTables && MeasuresGraphsWithTables.map((measuresPdf, i) => {
    const uniqueIdListing = _uniqueId('measures-listing-');

    return /*#__PURE__*/React.createElement(View, {
      break: measuresPdf.graphImage.breakSection && i !== 0,
      key: uniqueIdListing
    }, /*#__PURE__*/React.createElement(MeasuresListingTable, {
      graphImage: measuresPdf.graphImage,
      graphTableData: measuresPdf.graphTableData,
      listingsOnly: true,
      displaySectionTitle: measuresPdf.graphImage.breakSection
    }));
  }) || measuresPopulated && MeasuresRadioView === Both && MeasuresGraphsWithTables && MeasuresGraphsWithTables.map((measuresPdf, i) => {
    const uniqueIdCombination = _uniqueId('measures-graph-and-listing-');

    return /*#__PURE__*/React.createElement(View, {
      break: measuresPdf.graphImage.breakSection && i !== 0,
      key: uniqueIdCombination
    }, /*#__PURE__*/React.createElement(View, {
      style: styles.mYPointFiveCm
    }, /*#__PURE__*/React.createElement(Image, {
      source: measuresPdf.graphImage.src
    })), /*#__PURE__*/React.createElement(MeasuresListingTable, {
      graphImage: measuresPdf.graphImage,
      graphTableData: measuresPdf.graphTableData
    }));
  }) || /*#__PURE__*/React.createElement(NoMeasuresDataPDF, {
    dashboardPrintConfig: dashboardPrintConfig
  }), _ref);
};